import CC from '../../services/CCPublicationService';

export async function readRegionSiblings(snapshot, parent, signal) {
  const regions = []; const seen = new Set(); let cursor;
  do {
    if (signal.aborted) throw new Error('Cancelled');
    const page = await CC.regions(snapshot, parent, cursor, signal);
    regions.push(...page.regions); cursor = page.next_cursor;
    if (cursor && (seen.has(cursor) || seen.size >= 50)) throw new Error('CC region pagination limit');
    seen.add(cursor);
  } while (cursor);
  return regions;
}

// One instance belongs to one access identity and boundary revision. Cache metadata only.
export function createGeography(snapshot) {
  const children = new Map();
  const load = async (parent, signal) => {
    if (signal.aborted) throw new Error('Cancelled');
    const key = parent || '';
    if (!children.has(key)) {
      const rows = await readRegionSiblings(snapshot, parent, signal);
      if (signal.aborted) throw new Error('Cancelled');
      children.set(key, rows);
    }
    return children.get(key);
  };
  const validatePath = async (path, signal) => {
    const valid = [];
    for (const node of path) {
      const rows = await load(valid.at(-1)?.id, signal);
      const found = rows.find((row) => row.id === node.id);
      if (!found) break;
      valid.push(found);
    }
    return valid;
  };
  const resolve = async (target, hint, signal) => {
    const valid = await validatePath(hint || [], signal);
    const index = valid.findIndex((node) => node.id === target.id);
    if (index >= 0) return valid.slice(0, index + 1);
    if (valid.length && valid.at(-1).level !== 'VILLAGE') {
      const match = (await load(valid.at(-1).id, signal)).find((node) => node.id === target.id);
      if (match) return [...valid, match];
    }
    // Map boundaries carry parent_id. Resolve the country/province/district metadata,
    // then read only the target village's siblings, never every village in a country.
    const roots = await load(null, signal); let budget = 256;
    for (const root of roots) {
      if (root.id === target.id) return [root];
      for (const province of await load(root.id, signal)) {
        if (--budget < 0) throw new Error('CC hierarchy lookup limit');
        if (province.id === target.id) return [root, province];
        for (const district of await load(province.id, signal)) {
          if (district.id === target.id) return [root, province, district];
          if (district.id === target.parent_id) {
            const village = (await load(district.id, signal)).find((node) => node.id === target.id);
            if (village) return [root, province, district, village];
          }
        }
      }
    }
    throw new Error('CC region hierarchy unavailable');
  };
  return { load, validatePath, resolve };
}
